import React, { useState, useEffect } from "react";
import { 
  Button, 
  Input, 
  Card, 
  CardBody, 
  Chip, 
  Avatar,
  Divider
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faEnvelope, faPhone, faSave, faEdit } from "@fortawesome/free-solid-svg-icons";
import { getOnedata, UpdateOneData } from "../../../controllers/APIController";
import { toast } from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const Profile = () => {
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    Phone: "",
    ProfilePicture: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const clientQuery = useQueryClient();


  // Get user info
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const userId = userLogged && userLogged.user.id;

  // Load user profile data
  const { data: userData } = useQuery({
    queryKey: ["users", userId],
    queryFn: () => getOnedata("users", userId),
  });
  useEffect(() => {
    if (userData) {
      const profileData = {
        FirstName: userData.FirstName || "",
        LastName: userData.LastName || "",
        Email: userData.email || "",
        Phone: userData.Phone || "",
        ProfilePicture: userData.ProfilePictureUrl || "",
      };
      setFormData(profileData);
      setOriginalData(profileData);
    }
  }, [userData]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle save
  const handleSave = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const updateData = {
        FirstName: formData.FirstName,
        LastName: formData.LastName,
        Phone: formData.Phone
      };

      await UpdateOneData("users", userId, updateData);
      clientQuery.invalidateQueries({ queryKey: ["users", userId] });
      
      
      setOriginalData(formData);
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
  };

  // Check if form has changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faUser} className="text-emerald-600 text-lg" />
          <h3 className="text-base font-semibold text-slate-900">Profile Information</h3>
          <Chip color="success" variant="flat" size="sm">Active</Chip>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">
          Manage your personal profile information and contact details.
        </p>
      </div>

      {/* Profile Form */}
      <Card className="border border-slate-200 shadow-sm">
        <CardBody className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-slate-900">Personal Information</h4>
            {!isEditing && (
              <Button
                color="primary"
                size="sm"
                startContent={<FontAwesomeIcon icon={faEdit} />}
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </Button>
            )}
          </div>

          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <Avatar
                src={formData.ProfilePicture}
                name={`${formData.FirstName} ${formData.LastName}`}
                size="lg"
                className="w-20 h-20"
              />
              <div>
                <h5 className="font-medium text-slate-900">
                  {formData.FirstName} {formData.LastName}
                </h5>
                <p className="text-sm text-slate-500">{formData.Email}</p>
              </div>
            </div>

            <Divider />

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  First Name
                </label>
                <Input
                  placeholder="Enter your first name"
                  value={formData.FirstName}
                  onChange={(e) => handleFieldChange("FirstName", e.target.value)}
                  startContent={<FontAwesomeIcon icon={faUser} className="text-slate-400 text-xs" />}
                  size="sm"
                  isDisabled={!isEditing}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Last Name
                </label>
                <Input
                  placeholder="Enter your last name"
                  value={formData.LastName}
                  onChange={(e) => handleFieldChange("LastName", e.target.value)}
                  startContent={<FontAwesomeIcon icon={faUser} className="text-slate-400 text-xs" />}
                  size="sm"
                  isDisabled={!isEditing}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Email Address
                </label>
                <Input
                  placeholder="Enter your email"
                  value={formData.Email}
                  startContent={<FontAwesomeIcon icon={faEnvelope} className="text-slate-400 text-xs" />}
                  size="sm"
                  isDisabled={true}
                  description="Email cannot be changed"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Phone Number
                </label>
                <Input
                  placeholder="Enter your phone number"
                  value={formData.Phone}
                  onChange={(e) => handleFieldChange("Phone", e.target.value)}
                  startContent={<FontAwesomeIcon icon={faPhone} className="text-slate-400 text-xs" />}
                  size="sm"
                  isDisabled={!isEditing}
                />
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="flat"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  color="success"
                  startContent={<FontAwesomeIcon icon={faSave} />}
                  onClick={handleSave}
                  isLoading={isLoading}
                  disabled={isLoading || !hasChanges}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default Profile;
